import "@/App.css";
import { BrowserRouter, Routes, Route, NavLink, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import Dashboard from "@/pages/Dashboard";
import Reports from "@/pages/Reports";
import Compare from "@/pages/Compare";
import Login from "@/pages/Login";
import Admin from "@/pages/Admin";
import Team from "@/pages/Team";
import { AuthProvider, useAuth } from "@/auth/AuthContext";
import { isAdminHost, isClientHost } from "@/auth/portal";
import { Instagram, LogOut, Shield, Loader2, Eye, FileBarChart2, Zap, LayoutGrid, Users, GitCompareArrows } from "lucide-react";

const TOASTER_OPTIONS = {
  style: { background: "#FFFFFF", border: "1px solid #E2E8F0", color: "#0F172A" },
};

function AuthGate({ children, adminOnly = false, clientOnly = false }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <Loader2 size={20} className="text-pink-500 animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  // Route separation: super-admins live at /admin, clients live at /
  if (adminOnly && user.role !== "super_admin") return <Navigate to="/" replace />;
  if (clientOnly && user.role === "super_admin") return <Navigate to="/admin" replace />;
  return children;
}

function SidebarLink({ to, end, testid, icon: Icon, children }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testid}
      className={({ isActive }) =>
        `relative flex items-center gap-3 px-3.5 py-2.5 mx-2 rounded-lg text-xs transition-all duration-150 ${
          isActive
            ? "bg-pink-500/10 text-pink-400 font-semibold before:absolute before:left-0 before:top-2 before:bottom-2 before:w-1 before:bg-pink-500 before:rounded-r-full before:content-['']"
            : "text-slate-400 font-medium hover:text-white hover:bg-slate-800/60"
        }`
      }
    >
      <Icon size={15} />
      {children}
    </NavLink>
  );
}

function SidebarUserFooter({ roleLabel }) {
  const { user, logout } = useAuth();
  return (
    <div className="border-t border-slate-800 p-3 flex items-center gap-3" data-testid="nav-user-info">
      <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-pink-500 to-rose-400 text-white flex items-center justify-center text-xs font-bold shrink-0">
        {(user.email || "?")[0].toUpperCase()}
      </div>
      <div className="flex-1 min-w-0 leading-tight">
        <div className="text-[11px] font-semibold text-slate-200 truncate">{user.email}</div>
        <div className="text-[9px] font-mono uppercase tracking-widest text-slate-500">
          {roleLabel || user.org_name || "Client"}
        </div>
      </div>
      <button
        onClick={logout}
        className="p-1.5 rounded-lg text-slate-500 hover:text-red-400 hover:bg-slate-800 transition-colors"
        data-testid="nav-logout"
        title="Sign out"
      >
        <LogOut size={14} />
      </button>
    </div>
  );
}

function AdminShell() {
  const { workspaceOrg } = useAuth();
  const impersonating = !!workspaceOrg;
  return (
    <div className="min-h-screen bg-[#F4F5F8] flex">
      <aside className="w-60 shrink-0 bg-slate-900 border-r border-slate-800 flex flex-col sticky top-0 h-screen">
        <div className="h-16 flex items-center gap-2.5 px-5 border-b border-slate-800">
          <div className="p-1.5 rounded-lg bg-gradient-to-tr from-pink-500 to-rose-400 shadow-[0_8px_25px_-4px_rgba(236,72,153,0.4)]">
            <Shield size={15} className="text-white" />
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-lg font-bold text-white tracking-tight">CultureX</span>
            <span className="text-[9px] font-mono uppercase tracking-widest text-slate-500">Admin</span>
          </div>
        </div>
        <nav className="flex-1 py-3 overflow-y-auto" data-testid="top-nav">
          <div className="px-4 pt-2 pb-2 text-[10px] font-bold uppercase tracking-widest text-slate-500 font-mono">
            Admin
          </div>
          <SidebarLink to="/admin" end testid="nav-admin-workspaces" icon={LayoutGrid}>
            Workspaces
          </SidebarLink>
          {impersonating && (
            <div className="mx-2 mt-3 px-3 py-2 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-400 text-[10px] font-mono uppercase tracking-widest flex items-center gap-1.5"
                 data-testid="workspace-impersonation-badge">
              <Eye size={10} /> Viewing workspace
            </div>
          )}
        </nav>
        <SidebarUserFooter roleLabel="Super-admin" />
      </aside>
      <main className="flex-1 min-w-0">
        <Routes>
          <Route path="/" element={<Admin />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function ClientShell() {
  return (
    <div className="min-h-screen bg-[#F4F5F8] flex">
      <aside className="w-60 shrink-0 bg-slate-900 border-r border-slate-800 flex flex-col sticky top-0 h-screen">
        <div className="h-16 flex items-center gap-2.5 px-5 border-b border-slate-800">
          <div className="p-1.5 rounded-lg bg-gradient-to-tr from-pink-500 to-rose-400 shadow-[0_8px_25px_-4px_rgba(236,72,153,0.4)]">
            <Instagram size={15} className="text-white" />
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-lg font-bold text-white tracking-tight">CultureX</span>
            <span className="text-[9px] font-mono uppercase tracking-widest text-slate-500">Intel</span>
          </div>
        </div>
        <nav className="flex-1 py-3 overflow-y-auto" data-testid="top-nav">
          <div className="px-4 pt-2 pb-2 text-[10px] font-bold uppercase tracking-widest text-slate-500 font-mono">
            Tracking
          </div>
          <SidebarLink to="/" end testid="nav-reports" icon={FileBarChart2}>
            Reports
          </SidebarLink>
          <SidebarLink to="/quick" testid="nav-quick" icon={Zap}>
            Quick Scan
          </SidebarLink>
          <SidebarLink to="/compare" testid="nav-compare" icon={GitCompareArrows}>
            Compare
          </SidebarLink>
          <div className="px-4 pt-4 pb-2 text-[10px] font-bold uppercase tracking-widest text-slate-500 font-mono">
            Workspace
          </div>
          <SidebarLink to="/team" testid="nav-team" icon={Users}>
            Team
          </SidebarLink>
        </nav>
        <SidebarUserFooter />
      </aside>
      <main className="flex-1 min-w-0">
        <Routes>
          <Route path="/" element={<Reports />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/quick" element={<Dashboard />} />
          <Route path="/compare" element={<Compare />} />
          <Route path="/team" element={<Team />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            {/* Hostname-locked portals: on admin.* only /admin exists; on
                app.* only the client portal exists. On any other hostname
                both portals are reachable and role-based routing kicks in. */}
            {isAdminHost ? (
              <Route
                path="/*"
                element={<AuthGate adminOnly><AdminShell /></AuthGate>}
              />
            ) : isClientHost ? (
              <Route
                path="/*"
                element={<AuthGate clientOnly><ClientShell /></AuthGate>}
              />
            ) : (
              <>
                <Route
                  path="/admin/*"
                  element={<AuthGate adminOnly><AdminShell /></AuthGate>}
                />
                <Route
                  path="/*"
                  element={<AuthGate clientOnly><ClientShell /></AuthGate>}
                />
              </>
            )}
          </Routes>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="bottom-right" toastOptions={TOASTER_OPTIONS} />
    </div>
  );
}

export default App;
